// What the access token says this session may do.
//
// The token is the single source of authorization on both sides: RLS reads the
// same claims through has_perm() / in_scope(), so the UI and the database can
// never disagree about what is allowed. Querying `profiles` to decide what to
// render would reintroduce exactly that drift — the screen would offer a button
// the database then refuses.
//
// Decoding is not verification. Anyone can rewrite a JWT payload in devtools
// and make this file return whatever they like; all they get is a UI that
// offers buttons the server rejects. Enforcement lives in the policies. This
// exists so the app can hide what you cannot do, not to decide it.

import type { Session } from '@supabase/supabase-js';

export type LegacyRole = 'owner' | 'cashier';

export interface SessionClaims {
  /** Two-value role the pre-Phase-1 policies still read. */
  legacyRole: LegacyRole | null;
  /** Role id from `roles`, e.g. 'finance'. Null for a deactivated account. */
  roleId: string | null;
  /** Granted permissions as "module.action". */
  perms: ReadonlySet<string>;
  /** 1..3, or 0 when this role does not approve anything. */
  approvalLevel: number;
  /** Whether reads are limited to the user's own rayon. */
  scopeLimited: boolean;
  rayonId: string | null;
}

export const EMPTY_CLAIMS: SessionClaims = {
  legacyRole: null,
  roleId: null,
  perms: new Set(),
  approvalLevel: 0,
  scopeLimited: false,
  rayonId: null,
};

interface RawClaims {
  user_role?: unknown;
  role_id?: unknown;
  perms?: unknown;
  approval_level?: unknown;
  scope_limited?: unknown;
  rayon_id?: unknown;
}

/** base64url → JSON. Returns null on anything malformed rather than throwing. */
function decodePayload(token: string): RawClaims | null {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json) as RawClaims;
  } catch {
    return null;
  }
}

export function claimsFromSession(session: Session | null): SessionClaims {
  if (!session?.access_token) return EMPTY_CLAIMS;
  const raw = decodePayload(session.access_token);
  if (!raw) return EMPTY_CLAIMS;

  const legacy = raw.user_role;
  const perms = Array.isArray(raw.perms)
    ? new Set(raw.perms.filter((p): p is string => typeof p === 'string'))
    : new Set<string>();

  // A deactivated account is stamped with no claims at all — the hook strips
  // them — so every field below falls back to "can do nothing", which is the
  // same answer the database gives that session.
  return {
    legacyRole: legacy === 'owner' || legacy === 'cashier' ? legacy : null,
    roleId: typeof raw.role_id === 'string' ? raw.role_id : null,
    perms,
    approvalLevel:
      typeof raw.approval_level === 'number'
        ? raw.approval_level
        : Number(raw.approval_level) || 0,
    scopeLimited: raw.scope_limited === true,
    rayonId: typeof raw.rayon_id === 'string' ? raw.rayon_id : null,
  };
}

/** `can(claims, 'reports', 'export')` */
export function can(
  claims: SessionClaims | null | undefined,
  module: string,
  action: string,
): boolean {
  return claims?.perms.has(`${module}.${action}`) ?? false;
}

/** True when the role holds any permission at all on a module. */
export function canAny(
  claims: SessionClaims | null | undefined,
  module: string,
): boolean {
  if (!claims) return false;
  const prefix = `${module}.`;
  for (const p of claims.perms) if (p.startsWith(prefix)) return true;
  return false;
}
